import { Button } from "@progress/kendo-react-buttons";
// @ts-ignore
import Export from "@/components/Export";
import SearchBox from "@/components/SearchBox";
import {
  useAllUserGroups,
  useUserGroups,
  useUpdateUserGroup,
} from "@/hooks/useUserGroups";
import { RootState } from "@/store/store";
import { buildFilters, getPaginationData } from "@/utils/kendo";
import ToastService from "@/utils/toast";
import { columnProps } from "@/components/ColumnMenu";
import { queryKeys } from "@/constants/queryKeyFactory";
import { useSearchFilter } from "@/hooks/useSearchFilters";
import { queryClient } from "@/lib/queryClient";
import { userGroupSvc } from "@/services/userGroup.service";
//@ts-ignore
import { PagerTargetEvent } from "@progress/kendo-react-data-tools/pager/Pager";
import {
  ExcelExport,
  ExcelExportColumn,
} from "@progress/kendo-react-excel-export";
import {
  GridColumn as Column,
  Grid,
  GridDataStateChangeEvent,
  GridPageChangeEvent,
  GridToolbar,
} from "@progress/kendo-react-grid";
import {
  CheckboxChangeEvent,
  Switch,
  TextAreaChangeEvent,
  TextBoxChangeEvent,
} from "@progress/kendo-react-inputs";
import { Label } from "@progress/kendo-react-labels";
import { Plus } from "lucide-react";
import { createRef, useEffect, useMemo, useState } from "react";
import Filters from "../../constants/filters";
import IPageState from "../../interfaces/page.interface";
import { useAppSelector } from "../../store/hooks/hooks";
import WriteDrawer from "./WriteDrawer";
import { columnInterface, generateColumns } from "./columns";
import { IUserGroup } from "@/interfaces/userGroup.interface";
import { MultiSelectChangeEvent } from "@progress/kendo-react-dropdowns";

let mappingChanged = false;

export const Listing = () => {
  const { selectedProject } = useAppSelector(
    (state: RootState) => state.projects
  );

  const _exporter = createRef<ExcelExport>();
  const Pagination = getPaginationData();
  const updateUserGroupMutation = useUpdateUserGroup();

  const [addNewUserGroup, setAddNewUserGroup] = useState<IUserGroup | null>(
    null
  );
  const [columnsMapping, setColumnsMapping] = useState<any>(null);
  const [page, setPage] = useState<IPageState>(Pagination.initialDataState);
  const [pageSizeValue, setPageSizeValue] = useState<
    number | string | undefined
  >();
  const [showDrawer, setShowDrawer] = useState(false);
  const [userGroups, setUserGroups] = useState<IUserGroup[]>([]);
  const [stateColumns, setStateColumns] = useState<any>(
    generateColumns(
      handleEditClick,
      onColumnsSubmit,
      null,
      enableDisableUserGroup
    )
  );
  const [showArchived, setShowArchived] = useState(false);

  const { searchValue, dataState, handleSearchChange, setDataState } =
    useSearchFilter({
      initialState: Pagination.initialDataState,
      searchFields: ["name"],
    });

  const filterParams = useMemo(() => {
    const filters: any[] = [];

    // Project filter using array-contains
    if (selectedProject?.id && selectedProject.id !== "all") {
      filters.push({
        logic: "and" as const,
        filters: [
          {
            field: "Projects",
            operator: "array-contains",
            value: selectedProject.id,
          },
        ],
      });
    }

    // Active/Archived filter
    if (!showArchived) {
      filters.push({
        logic: "and" as const,
        filters: [
          {
            field: "isActive",
            operator: "eq",
            value: true,
          },
        ],
      });
    }

    // Add existing filters from dataState if any
    if (dataState.filter?.filters?.length) {
      dataState.filter.filters.forEach((filter: any) => {
        if ("logic" in filter && "filters" in filter) {
          filters.push(filter);
        } else {
          filters.push({
            logic: "and" as const,
            filters: [filter],
          });
        }
      });
    }

    return {
      pageNumber: page.skip,
      pageSize: page.take,
      sort: dataState.sort ?? null,
      filter: {
        logic: "and" as const,
        filters,
      },
    };
  }, [
    page.skip,
    page.take,
    dataState.sort,
    dataState.filter,
    dataState.filter,
    selectedProject?.id,
    showArchived,
  ]);

  const { data: userGroupsData, refetch } = useUserGroups(filterParams, {
    enabled: !!filterParams,
  });

  const { data: allUserGroupsData, refetch: refetchAllUserGroups } =
    useAllUserGroups(
      { filter: buildFilters(selectedProject?.id) },
      { enabled: false }
    );

  const excelExport = () => {
    if (_exporter.current) {
      if (userGroupsData?.data && userGroupsData.data.length > 0) {
        _exporter.current.save(userGroupsData?.data);
      } else {
        refetch().then(() => {
          setTimeout(() => {
            _exporter.current?.save(userGroupsData?.data || []);
          }, 300);
        });
      }
    }
  };

  function onColumnsSubmit(columnsState: Array<columnInterface>) {
    const columnsMapping: any = [];
    for (let col of columnsState) {
      if (col.show) {
        columnsMapping.push(col.field);
      }
    }
    setColumnsMapping(columnsMapping);
    mappingChanged = true;
    const cols = generateColumns(
      handleEditClick,
      onColumnsSubmit,
      columnsState,
      enableDisableUserGroup
    );
    setStateColumns(cols);
  }

  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take =
      targetEvent.value === "All" ? userGroups.length : event.page.take;

    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({
      ...event.page,
      take,
    });
  };

  const handleChange = (
    e:
      | TextBoxChangeEvent
      | TextAreaChangeEvent
      | CheckboxChangeEvent
      | MultiSelectChangeEvent
  ) => {
    if (e.target.name === "levels") {
      setAddNewUserGroup((prev: any) => {
        return { ...prev, levels: e.value?.map((item: any) => item.id) };
      });
      return;
    }

    if (e.target.name === "users") {
      setAddNewUserGroup((prev: any) => {
        return { ...prev, users: e.value?.map((item: any) => item.id) };
      });
      return;
    }

    if (e.target.name === "engineers") {
      setAddNewUserGroup((prev: any) => {
        return { ...prev, engineers: e.value?.map((item: any) => item.id) };
      });
      return;
    }

    setAddNewUserGroup((prev: any) => {
      return { ...prev, [e.target.name as any]: e.target.value };
    });
  };

  const dataStateChange = (event: GridDataStateChangeEvent) => {
    setDataState(event.dataState);
  };

  const createUserGroup = () => {
    if (!addNewUserGroup) return;

    const userGroupData = {
      ...addNewUserGroup,
      projects: selectedProject?.id ? [selectedProject.id] : [],
      isActive: true,
      isDeleted: false,
    };

    userGroupSvc.create<IUserGroup>(userGroupData)
      .then((res) => {
        if (!res?.error) {
          ToastService.success("User Group created successfully");
          queryClient.invalidateQueries({ queryKey: queryKeys.userGroup.root });
          setShowDrawer(false);
          return;
        }

        ToastService.error(res?.error?.message || "Something went wrong");
      })
      .catch((error) => {
        // Error is already shown via toast in base service
        console.error("Error creating user group:", error);
      });
  };

  const editUserGroup = async () => {
    if (!addNewUserGroup?.id) return;
    const userGroupData = { ...addNewUserGroup };

    if (!userGroupData?.projects) {
      userGroupData.projects = selectedProject?.id ? [selectedProject.id] : [];
    } else if (userGroupData?.projects?.length === 0) {
      userGroupData.projects = selectedProject?.id ? [selectedProject.id] : [];
    } else if (userGroupData?.projects?.length > 0) {
      const selectedProjectId = userGroupData?.projects?.find(
        (item: any) => item === selectedProject?.id
      );
      if (!selectedProjectId) {
        userGroupData.projects = [...userGroupData.projects, selectedProject?.id!];
      }
    }

    updateUserGroupMutation.mutate(
      { id: userGroupData.id!, data: userGroupData },
      {
        onSuccess: () => {
          ToastService.success("User Group updated successfully");
          setShowDrawer(false);
        },
        onError: (error) => {
          // Error is already shown via toast in base service
          console.error("Error updating user group:", error);
        },
      }
    );
  };

  function enableDisableUserGroup(dataItem: IUserGroup) {
    if (!dataItem?.id) return;
    updateUserGroupMutation.mutate(
      { id: dataItem.id, data: { ...dataItem, isActive: !dataItem.isActive } },
      {
        onSuccess: () => {
          ToastService.success("User Group updated successfully");
        },
      }
    );
  }

  function handleEditClick(data: IUserGroup) {
    setAddNewUserGroup(data);
    setShowDrawer(true);
  }

  useEffect(() => {
    if (!showDrawer) {
      setAddNewUserGroup(null);
    }
  }, [showDrawer]);

  useEffect(() => {
    if (userGroupsData?.data) {
      setUserGroups(userGroupsData.data);
    }
  }, [userGroupsData]);

  useEffect(() => {
    if (!userGroups?.length || !columnsMapping) return;
    const cols = generateColumns(
      handleEditClick,
      onColumnsSubmit,
      null,
      enableDisableUserGroup
    );
    setStateColumns(cols);
  }, [userGroups, columnsMapping]);

  return (
    <>
      <div className="setting-page">
        <Grid
          style={{ maxHeight: "" }}
          data={userGroups}
          {...dataState}
          onDataStateChange={dataStateChange}
          skip={page.skip}
          take={page.take}
          total={userGroupsData?.total || 0}
          pageable={{
            ...Pagination.pageSizeValue,
            pageSizeValue: pageSizeValue,
          }}
          onPageChange={pageChange}
          className={`customHeight employee`}
          resizable={Filters.resizeable}
          selectable={{
            enabled: true,
            drag: true,
            mode: "multiple",
          }}
        >
          <GridToolbar>
            <div className="flex gap-3 w-full items-center">
              <div className="w-[250px]">
                <SearchBox
                  value={searchValue}
                  onChange={handleSearchChange}
                  placeholder="Search"
                />
              </div>
              <Button
                themeColor={"primary"}
                className="border border-[#E2E8F0]"
                onClick={() => setShowDrawer(true)}
              >
                <span className="inline-flex items-center gap-2 font-normal">
                  <Plus className="w-4 h-4" /> Add New
                </span>
              </Button>

              <div className="flex items-center gap-2 md:order-3 sm:order-2 order-4 sm:mt-0 mt-2 sm:ml-0 ml-auto">
                <Label className="font-medium text-sm font-cabin">
                  Show Inactive
                </Label>
                <Switch
                  defaultChecked={false}
                  onChange={(e) => {
                    setShowArchived(!showArchived);
                  }}
                />
              </div>
              <div
                className="cursor-pointer sm:ml-0 ml-auto sm:flex-initial flex-1"
                onClick={excelExport}
              >
                <Export />
                <ExcelExport
                  data={allUserGroupsData?.data || []}
                  collapsible={true}
                  fileName="UserGroups.xlsx"
                  ref={_exporter}
                >
                  {stateColumns &&
                    stateColumns.length > 0 &&
                    stateColumns.map((column: any, idx: number) => {
                      if (column.show) {
                        return (
                          <ExcelExportColumn
                            key={idx}
                            field={column.field}
                            title={column.title}
                          />
                        );
                      }
                    })}
                </ExcelExport>
              </div>
            </div>
          </GridToolbar>
          {stateColumns &&
            stateColumns.length > 0 &&
            stateColumns.map((column: any, idx: number) => {
              if (column.show) {
                if (column.cells) {
                  return (
                    <Column
                      {...columnProps(column.field, dataState)}
                      key={idx}
                      field={column.field}
                      title={column.title}
                      columnMenu={column.columnMenu}
                      minResizableWidth={200}
                      cells={column.cells}
                      filter={column.filter ? column.filter : "text"}
                    />
                  );
                }
                return (
                  <Column
                    {...columnProps(column.field, dataState)}
                    key={idx}
                    field={column.field}
                    title={column.title}
                    columnMenu={column.columnMenu}
                    minResizableWidth={200}
                    filter={column.filter ? column.filter : "text"}
                  />
                );
              }
            })}
        </Grid>
      </div>
      <WriteDrawer
        showCreateDrawer={showDrawer}
        setShowCreateDrawer={setShowDrawer}
        addNewUserGroup={addNewUserGroup}
        handleChange={handleChange}
        onSave={addNewUserGroup?.id ? editUserGroup : createUserGroup}
      />
    </>
  );
};
